import { useImmer } from "use-immer";
import { useEffect } from "react";
import { errorMessage, successMessage } from "../../../utils";
import { useDebounce } from "use-debounce";
import { usePermission } from "../../../shared";
import {
  getStampCardProductsApi,
  deleteStampCardProductApi,
  assignProductToStampCardApi,
} from "../api";
import { getCategorywiseProductsInStore } from "../..";

export const useStampCardProducts = ({ stampCardId }) => {
  const isEditable = usePermission("stamp-card-modify");
  const isDeletable = usePermission("stamp-card-delete");
  const isCreateVisible = usePermission("stamp-card-create");
  const isVisible = usePermission("stamp-card-view");

  const [state, setState] = useImmer({
    isBusy: false,
    isDelete: false,
    id: null,
    isOpen: false,
    initialLoad: true,
    productDetails: {
      isLoading: true,
      isSaveButtonBusy: false,
      isOpen: false,
      isDelete: false,
      id: null,
      productList: [],
      allProducts: [],
      filteredProducts: [],
      selectedProducts: [],
      allCategoryProductList: [],
      isModalLoading: true,
    },
    filters: {
      isSearching: false,
      searchText: "",
      category: "",
    },
    data: [],
  });

  const [debouncedText] = useDebounce(state.filters.searchText, 1000);

  // Fetch products assigned to stamp card
  useEffect(() => {
    if (stampCardId) {
      getStampCardProducts(stampCardId);
    }
  }, [stampCardId]);

  const getStampCardProducts = async (
    stampCardId,
    search = null,
    category = null,
  ) => {
    try {
      setState((draft) => {
        draft.isBusy = true;
        draft.initialLoad = true;
      });
      const res = await getStampCardProductsApi(stampCardId, {
        search,
        category,
      });
      if (res.success) {
        setState((draft) => {
          draft.data = res.data;
          draft.productDetails.productList = res.data;
          draft.productDetails.selectedProducts = res.data.map(
            (item) => item.id,
          );
          draft.isBusy = false;
          draft.initialLoad = false;
        });
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message || "Failed to load products");
      setState((draft) => {
        draft.isBusy = false;
        draft.initialLoad = false;
      });
    }
  };

  // Debounced search
  useEffect(() => {
    if (!state.initialLoad && !state.filters.isSearching) {
      const { category } = state.filters;
      getStampCardProducts(stampCardId, debouncedText, category);
    }
  }, [debouncedText]);

  const DeleteModals = (isOpen, productId) => {
    setState((draft) => {
      draft.isDelete = isOpen;
      draft.productDetails.id = productId;
    });
  };

  const handleDeleteProducts = async (productId) => {
    try {
      setState((draft) => {
        draft.productDetails.isSaveButtonBusy = true;
      });
      const res = await deleteStampCardProductApi(
        stampCardId,
        state.productDetails.id,
      );
      if (res.success) {
        successMessage("Product removed successfully");
        setState((draft) => {
          draft.productDetails.productList =
            draft.productDetails.productList.filter(
              (item) => item.id !== state.productDetails.id,
            );
          draft.productDetails.isSaveButtonBusy = false;
        });
        DeleteModals(false, null);
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message || "Failed to delete product");
      setState((draft) => {
        draft.productDetails.isSaveButtonBusy = false;
      });
    }
  };

  const handleModals = (isOpen, productId) => {
    setState((draft) => {
      draft.isOpen = isOpen;
    });
    if (isOpen) {
      HandleFilterProduct("initial");
    }
  };

  const HandleFilterProduct = (type, text) => {
    const ProductList = state.productDetails.allCategoryProductList
      .map((val) => {
        let filtered = val.products.filter((data) => {
          if (!["delivery", "gift_card"].includes(data.type)) return true;
        });
        return filtered.length ? { ...val, products: filtered } : undefined;
      })
      .filter(Boolean);

    if (type === "initial") {
      setState((draft) => {
        draft.productDetails.filteredProducts = ProductList;
      });
    } else {
      const Search = (data, text) => {
        text = text.toLowerCase();
        return data
          .map((val) => {
            let filtered = val.products.filter((data) => {
              if (data.name.toString().toLowerCase().indexOf(text) > -1)
                return true;
            });
            return filtered.length ? { ...val, products: filtered } : undefined;
          })
          .filter(Boolean);
      };

      setState((draft) => {
        draft.productDetails.filteredProducts = Search(ProductList, text);
      });
    }
  };

  useEffect(() => {
    HandleFilterProduct("initial");
  }, [
    state.productDetails.allCategoryProductList,
    state.productDetails.productList,
  ]);

  const handleProductSelect = (type, value, event) => {
    if (type === "all") {
      if (event.target.checked) {
        setState((draft) => {
          draft.productDetails.selectedProducts = value.flatMap((cat) =>
            cat.products.map((prod) => prod.id),
          );
        });
      } else {
        setState((draft) => {
          draft.productDetails.selectedProducts = [];
        });
      }
    } else if (type === "body") {
      if (event.target.checked) {
        setState((draft) => {
          draft.productDetails.selectedProducts = [
            ...draft.productDetails.selectedProducts,
            value.id,
          ];
        });
      } else {
        setState((draft) => {
          draft.productDetails.selectedProducts =
            draft.productDetails.selectedProducts.filter(
              (val) => val !== value.id,
            );
        });
      }
    } else {
      const subRowsID = value.products?.map((e) => e.id);

      if (event.target.checked) {
        setState((draft) => {
          draft.productDetails.selectedProducts = [
            ...new Set([
              ...state.productDetails.selectedProducts,
              ...subRowsID,
            ]),
          ];
        });
      } else {
        setState((draft) => {
          draft.productDetails.selectedProducts =
            draft.productDetails.selectedProducts.filter(
              (val) => !subRowsID.includes(val),
            );
        });
      }
    }
  };

  const handleAssignProductToStampCard = async () => {
    const payload = {
      product_id: state.productDetails.selectedProducts,
    };

    setState((draft) => {
      draft.productDetails.isSaveButtonBusy = true;
    });

    try {
      const res = await assignProductToStampCardApi(stampCardId, payload);
      if (res.success) {
        successMessage(res.message || "Products assigned successfully");
        setState((draft) => {
          draft.productDetails.productList = [
            ...draft.productDetails.productList,
            ...draft.productDetails.allProducts.filter((val) =>
              draft.productDetails.selectedProducts.includes(val.id),
            ),
          ];
          draft.productDetails.isLoading = false;
          draft.productDetails.isSaveButtonBusy = false;
        });
        handleModals(false);
        getStampCardProducts(stampCardId);
      }
    } catch (error) {
      errorMessage(
        error?.response?.data?.message || "Failed to assign products",
      );
      setState((draft) => {
        draft.productDetails.isSaveButtonBusy = false;
      });
    }
  };

  const onChangeFilter = (e) => {
    const { name, value } = e.target;
    const { searchText, category } = state.filters;

    if (name === "category") {
      getStampCardProducts(stampCardId, searchText, value);
    }

    setState((draft) => {
      draft.filters[name] = value;
      draft.filters.isSearching = false;
    });
  };

  const resetFilters = () => {
    if (state.filters.category || state.filters.searchText) {
      getStampCardProducts(stampCardId);
      setState((draft) => {
        draft.filters.category = "";
        draft.filters.searchText = "";
        draft.filters.isSearching = true;
      });
    }
  };

  const handleSelectAllProducts = (checked) => {
    if (checked) {
      setState((draft) => {
        draft.productDetails.selectedProducts =
          draft.productDetails.filteredProducts.flatMap((item) =>
            item.products.map((prod) => prod.id),
          );
      });
    } else {
      setState((draft) => {
        draft.productDetails.selectedProducts = [];
      });
    }
  };

  // Fetch all category-wise products
  useEffect(() => {
    if (stampCardId) {
      getCategorywiseProductList();
    }
  }, [stampCardId]);

  const getCategorywiseProductList = async () => {
    try {
      setState((draft) => {
        draft.isBusy = true;
      });
      const res = await getCategorywiseProductsInStore();
      if (res.success) {
        const filteredCategories = (res.data || [])
          .map((cat) => ({
            ...cat,
            products: (cat.products || []).filter(
              (prod) => prod.type !== "modifier",
            ),
          }))
          .filter((cat) => cat.products.length > 0);

        setState((draft) => {
          draft.isBusy = false;
          draft.productDetails.allCategoryProductList = filteredCategories;
          draft.productDetails.allProducts = filteredCategories.flatMap(
            (cat) => cat.products,
          );
        });
      }
    } catch (err) {
      errorMessage(
        err?.response?.data?.message || "Failed to load category products",
      );
      setState((draft) => {
        draft.isBusy = false;
      });
    }
  };

  return {
    state,
    DeleteModals,
    handleDeleteProducts,
    handleModals,
    handleProductSelect,
    HandleFilterProduct,
    handleAssignProductToStampCard,
    onChangeFilter,
    resetFilters,
    isCreateVisible,
    isDeletable,
    isEditable,
    isVisible,
    handleSelectAllProducts,
  };
};
